/** Raw fetch calls. Query hooks in `queries.ts` wrap these with react-query. */

import { request } from '../lib/http'
import type {
  BatchAction,
  BatchResponse,
  DailyReviewResponse,
  DailySummaryResponse,
  EntriesResponse,
  EntryDetail,
  HardwareBomCreateInput,
  HardwareBomItem,
  HardwareBomUpdateInput,
  HardwarePricesResponse,
  HardwareProject,
  HardwareProjectInput,
  HealthMetric,
  HealthMetricInput,
  HealthMetricQuery,
  HealthMetricUpdateInput,
  HealthReport,
  HealthReportInput,
  InboxItem,
  InboxResponse,
  MessagesResponse,
  SearchKbResponse,
  Stats,
  V3InboxResponse,
  V3ResourceDetail,
  V3ResourcesResponse,
} from './types'

export function listInbox(status: string, limit?: number): Promise<InboxResponse> {
  const params = new URLSearchParams({ status })
  if (limit !== undefined) params.set('limit', String(limit))
  return request<InboxResponse>(`/api/kb/inbox?${params.toString()}`)
}

export function fetchInboxItem(id: string): Promise<InboxItem> {
  return request<InboxItem>(`/api/kb/inbox/${encodeURIComponent(id)}`)
}

export function runInboxBatch(args: {
  ids: string[]
  action: BatchAction
  dryRun?: boolean
}): Promise<BatchResponse> {
  return request<BatchResponse>('/api/kb/inbox/batch', {
    method: 'POST',
    body: JSON.stringify({ ids: args.ids, action: args.action, dry_run: args.dryRun ?? false }),
  })
}

export type EntryFilters = {
  domain?: string
  track?: string
  status?: string
  q?: string
}

export function listEntries(filters: EntryFilters): Promise<EntriesResponse> {
  const params = new URLSearchParams()
  if (filters.domain) params.set('domain', filters.domain)
  if (filters.track) params.set('track', filters.track)
  if (filters.status) params.set('status', filters.status)
  if (filters.q) params.set('q', filters.q)
  return request<EntriesResponse>(`/api/kb/entries?${params.toString()}`)
}

export function fetchEntry(id: string): Promise<EntryDetail> {
  return request<EntryDetail>(`/api/kb/entries/${encodeURIComponent(id)}`)
}

export function fetchDailyReview(): Promise<DailyReviewResponse> {
  return request<DailyReviewResponse>('/api/kb/review/daily')
}

export function fetchDailySummary(date?: string): Promise<DailySummaryResponse> {
  const query = date ? `?date=${encodeURIComponent(date)}` : ''
  return request<DailySummaryResponse>(`/api/kb/review/summary${query}`)
}

export function searchKb(q: string, limit?: number): Promise<SearchKbResponse> {
  const params = new URLSearchParams({ q })
  if (limit !== undefined) params.set('limit', String(limit))
  return request<SearchKbResponse>(`/api/kb/search?${params.toString()}`)
}

export function submitReviewFeedback(id: string, remembered: boolean): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>('/api/kb/review/feedback', {
    method: 'POST',
    body: JSON.stringify({ id, remembered }),
  })
}

export function listMessages(unreadOnly: boolean): Promise<MessagesResponse> {
  return request<MessagesResponse>(`/api/kb/messages?unread_only=${unreadOnly ? 'true' : 'false'}`)
}

export function markMessagesRead(ids: string[], all: boolean): Promise<{ updated: number; unread: number }> {
  return request<{ updated: number; unread: number }>('/api/kb/messages/read', {
    method: 'POST',
    body: JSON.stringify({ ids, all }),
  })
}

export function fetchStats(): Promise<Stats> {
  return request<Stats>('/api/kb/stats')
}

// ---------------------------------------------------------------------------
// v3 知识数据：资源列表 / 详情 / 收件箱（只读 Supabase）
// ---------------------------------------------------------------------------

export function listV3Resources(page = 1, pageSize = 20): Promise<V3ResourcesResponse> {
  const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) })
  return request<V3ResourcesResponse>(`/api/kb/v3/resources?${params.toString()}`)
}

export function fetchV3ResourceDetail(id: string): Promise<V3ResourceDetail> {
  return request<V3ResourceDetail>(`/api/kb/v3/resources/${encodeURIComponent(id)}`)
}

/** @deprecated 使用 fetchV3ResourceDetail；保留别名避免旧引用断裂。 */
export const fetchV3Resource = fetchV3ResourceDetail

export function listV3Inbox(status: string, page = 1, pageSize = 20): Promise<V3InboxResponse> {
  const params = new URLSearchParams({
    status,
    page: String(page),
    page_size: String(pageSize),
  })
  return request<V3InboxResponse>(`/api/kb/v3/inbox?${params.toString()}`)
}

// ---------------------------------------------------------------------------
// 硬件：项目 / BOM / 价格基线
// ---------------------------------------------------------------------------

export function listHardwareProjects(): Promise<HardwareProject[]> {
  return request<HardwareProject[]>('/api/hardware/projects')
}

export function fetchHardwareProject(id: string): Promise<HardwareProject> {
  return request<HardwareProject>(`/api/hardware/projects/${encodeURIComponent(id)}`)
}

export function createHardwareProject(input: HardwareProjectInput): Promise<HardwareProject> {
  return request<HardwareProject>('/api/hardware/projects', {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

export function updateHardwareProject(id: string, input: Partial<HardwareProjectInput>): Promise<HardwareProject> {
  return request<HardwareProject>(`/api/hardware/projects/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body: JSON.stringify(input),
  })
}

export function deleteHardwareProject(id: string): Promise<{ ok: boolean; id: string }> {
  return request<{ ok: boolean; id: string }>(`/api/hardware/projects/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  })
}

export function listHardwareBom(projectId: string): Promise<HardwareBomItem[]> {
  return request<HardwareBomItem[]>(`/api/hardware/projects/${encodeURIComponent(projectId)}/bom`)
}

export function createHardwareBom(input: HardwareBomCreateInput): Promise<HardwareBomItem> {
  return request<HardwareBomItem>('/api/hardware/bom', {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

export function updateHardwareBom(id: string, input: HardwareBomUpdateInput): Promise<HardwareBomItem> {
  return request<HardwareBomItem>(`/api/hardware/bom/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body: JSON.stringify(input),
  })
}

export function deleteHardwareBom(id: string): Promise<{ ok: boolean; id: string }> {
  return request<{ ok: boolean; id: string }>(`/api/hardware/bom/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  })
}

export function fetchHardwarePrices(): Promise<HardwarePricesResponse> {
  return request<HardwarePricesResponse>('/api/hardware/prices')
}

// ---------------------------------------------------------------------------
// 健康：指标（date + metric 复合键）/ 体检报告
// ---------------------------------------------------------------------------

export function listHealthMetrics(query: HealthMetricQuery = {}): Promise<HealthMetric[]> {
  const params = new URLSearchParams()
  if (query.metric) params.set('metric', query.metric)
  if (query.start) params.set('start', query.start)
  if (query.end) params.set('end', query.end)
  const suffix = params.toString() ? `?${params.toString()}` : ''
  return request<HealthMetric[]>(`/api/health/metrics${suffix}`)
}

export function createHealthMetric(input: HealthMetricInput): Promise<HealthMetric> {
  return request<HealthMetric>('/api/health/metrics', {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

export function updateHealthMetric(
  key: { date: string; metric: string },
  input: HealthMetricUpdateInput,
): Promise<HealthMetric> {
  return request<HealthMetric>('/api/health/metrics', {
    method: 'PUT',
    body: JSON.stringify({ ...key, ...input }),
  })
}

export function deleteHealthMetric(key: { date: string; metric: string }): Promise<{ ok: boolean }> {
  const params = new URLSearchParams({ date: key.date, metric: key.metric })
  return request<{ ok: boolean }>(`/api/health/metrics?${params.toString()}`, {
    method: 'DELETE',
  })
}

export function listHealthReports(): Promise<HealthReport[]> {
  return request<HealthReport[]>('/api/health/reports')
}

export function fetchHealthReport(id: string): Promise<HealthReport> {
  return request<HealthReport>(`/api/health/reports/${encodeURIComponent(id)}`)
}

export function createHealthReport(input: HealthReportInput): Promise<HealthReport> {
  return request<HealthReport>('/api/health/reports', {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

export function updateHealthReport(id: string, input: Partial<HealthReportInput>): Promise<HealthReport> {
  return request<HealthReport>(`/api/health/reports/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body: JSON.stringify(input),
  })
}

export function deleteHealthReport(id: string): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(`/api/health/reports/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  })
}
